import React from "react"
import { Button } from "antd"
import { CheckOutlined } from "@ant-design/icons"
import dayjs from "dayjs"
import clsx from "clsx"
import type {
    ReviewDetail,
    ReviewWorktime,
    SignedReviewDetail,
    SignedReviewFormValue,
    UnsignedReviewDetail,
    UnsignedReviewFormValue,
} from "../types"
import {
    NON_BRAND_REASONS,
    SIGNED_REASON_LABEL,
    UNSIGNED_REASONS,
    normalizeSignedReasonTypes,
} from "../constants"
import {
    AttachmentList,
    ExternalLink,
    MilestoneTable,
    Money,
    PageHeader,
    Percent,
    SectionCard,
    formatHours,
    formatMoney,
    formatPercent,
    text,
} from "./shared"

/** 只读信息项（标签 + 值） */
const ReadItem: React.FC<{ label: React.ReactNode; children: React.ReactNode; className?: string }> = ({
    label,
    children,
    className,
}) => (
    <div className={clsx("min-w-0", className)}>
        <div className="text-xs text-gray-400 mb-1">{label}</div>
        <div className="text-sm text-gray-800 font-medium break-words">{children}</div>
    </div>
)

/** 只读分块（小标题 + 内容） */
const ReadBlock: React.FC<{ title: React.ReactNode; children: React.ReactNode }> = ({ title, children }) => (
    <div className="rounded-lg border border-gray-200 p-4">
        <div className="text-sm font-medium text-gray-800">{title}</div>
        <div className="mt-3">{children}</div>
    </div>
)

/** A1 已勾选人员只读表格（勾选状态 / 工号 / 姓名 / 专业 / 公司 / 工日数 / 类型 / 修正后的工日数） */
const A1WorktimeTable: React.FC<{ list: ReviewWorktime[] }> = ({ list }) => (
    <div className="overflow-x-auto">
        <table className="w-full min-w-[880px] text-sm">
            <thead>
                <tr className="text-left text-xs text-gray-400 bg-gray-50">
                    {["勾选状态", "工号", "姓名", "专业", "公司", "工日数", "类型", "修正后的工日数"].map(
                        (h) => (
                            <th key={h} className="font-normal px-3 py-2 whitespace-nowrap">
                                {h}
                            </th>
                        )
                    )}
                </tr>
            </thead>
            <tbody>
                {list.map((w) => (
                    <tr key={`${w.clerkCode}-${w.id}`} className="border-t border-gray-100">
                        <td className="px-3 py-2">
                            <span className="inline-flex h-4 w-4 items-center justify-center rounded border border-dark-bg bg-dark-bg text-white">
                                <CheckOutlined style={{ fontSize: 10 }} />
                            </span>
                        </td>
                        <td className="px-3 py-2 text-gray-700">{text(w.clerkCode)}</td>
                        <td className="px-3 py-2 text-gray-700">{text(w.name)}</td>
                        <td className="px-3 py-2 text-gray-500">{text(w.majorName)}</td>
                        <td className="px-3 py-2 text-gray-500">{text(w.companyName)}</td>
                        <td className="px-3 py-2 text-right text-gray-700">{formatHours(w.workday ?? w.totalHours)}</td>
                        <td className="px-3 py-2 text-gray-500">{w.source === "manual" ? "手动添加" : "系统"}</td>
                        <td className="px-3 py-2 text-right text-gray-700">
                            {w.correctedTotalHours === undefined || w.correctedTotalHours === null
                                ? "-"
                                : formatHours(w.correctedTotalHours)}
                        </td>
                    </tr>
                ))}
                {list.length === 0 && (
                    <tr>
                        <td colSpan={8} className="px-3 py-6 text-center text-gray-400">
                            未勾选人员
                        </td>
                    </tr>
                )}
            </tbody>
        </table>
    </div>
)

/** 只读页头（已提交状态 + 提交时间） */
const PreviewHeader: React.FC<{ detail: ReviewDetail; onBack: () => void }> = ({ detail, onBack }) => (
    <PageHeader
        title={detail.kind === "signed" ? "已签约项目毛利复盘" : "未签约项目人力投入复盘"}
        subtitle={`${detail.projectName}（${detail.projectNo}）`}
        onBack={onBack}
        extra={
            <div className="flex items-center gap-3 shrink-0">
                <span className="rounded border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs text-emerald-600">
                    已提交
                </span>
                <span className="text-xs text-gray-400">
                    {detail.savedSubmission?.submittedAt
                        ? dayjs(detail.savedSubmission.submittedAt).format("YYYY-MM-DD HH:mm")
                        : "-"}
                </span>
            </div>
        }
    />
)

/** 只读底部栏：填报人 + 提交日期（取提交时间，非当天）+ 返回（无暂存 / 提交） */
const PreviewFooter: React.FC<{ detail: ReviewDetail; onBack: () => void }> = ({ detail, onBack }) => (
    <div className="sticky bottom-0 bg-white/95 backdrop-blur border-t border-gray-200 px-5 py-3 flex flex-wrap items-center gap-4">
        <div className="flex flex-wrap items-center gap-6 text-sm text-gray-600">
            <span>
                填报人（管理所长）：<span className="text-gray-800 font-medium">{detail.manager || "-"}</span>
            </span>
            <span>
                提交日期：
                <span className="text-gray-800 font-medium">
                    {detail.savedSubmission?.submittedAt
                        ? dayjs(detail.savedSubmission.submittedAt).format("YYYY-MM-DD")
                        : "-"}
                </span>
            </span>
        </div>
        <div className="ml-auto">
            <Button onClick={onBack}>返回</Button>
        </div>
    </div>
)

/** 已签约：项目信息（与表单同结构，只读） */
const SignedProjectInfo: React.FC<{ detail: SignedReviewDetail }> = ({ detail }) => {
    const { metrics } = detail
    return (
        <section className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <header className="bg-[#1c4d38] px-5 py-3">
                <h3 className="text-[15px] leading-6 font-semibold text-white">项目信息</h3>
            </header>
            <div className="p-5">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-8 gap-y-5 mb-6">
                    {[
                        { label: "项目名称", value: detail.projectName },
                        { label: "项目编号", value: detail.projectNo },
                        { label: "所属所 / 管理所长", value: `${detail.department} / ${detail.manager}` },
                        { label: "复盘发送月份", value: detail.reviewMonth },
                        { label: "指标", value: metrics.target },
                    ].map((it) => (
                        <ReadItem key={it.label} label={it.label}>
                            {text(it.value)}
                        </ReadItem>
                    ))}
                </div>

                <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <table className="w-full min-w-[840px] border-separate border-spacing-0 text-sm">
                        <thead>
                            <tr>
                                <th className="w-32 bg-dark-bg px-4 py-2.5 text-left font-medium text-white">
                                    指标
                                </th>
                                {["完成产值", "完成产值毛利", "完成产值毛利率", "实收产值", "实收毛利率"].map((h) => (
                                    <th
                                        key={h}
                                        className="bg-dark-bg px-4 py-2.5 text-left font-medium text-white"
                                    >
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {metrics.periods.map((p) => (
                                <tr key={p.label} className="hover:bg-gray-50">
                                    <td className="px-4 py-2.5 text-gray-700 border-t border-gray-100">
                                        {p.label}
                                    </td>
                                    <td className="px-4 py-2.5 font-medium border-t border-gray-100">
                                        <Money value={p.completionOutput} />
                                    </td>
                                    <td className="px-4 py-2.5 font-medium border-t border-gray-100">
                                        <Money value={p.completionProfit} />
                                    </td>
                                    <td className="px-4 py-2.5 font-medium border-t border-gray-100">
                                        <Percent value={p.completionProfitRate} />
                                    </td>
                                    <td className="px-4 py-2.5 font-medium border-t border-gray-100">
                                        <Money value={p.actualIncome} />
                                    </td>
                                    <td className="px-4 py-2.5 font-medium border-t border-gray-100">
                                        <Percent value={p.actualProfitRate} />
                                    </td>
                                </tr>
                            ))}
                            {metrics.periods.length === 0 && (
                                <tr>
                                    <td
                                        colSpan={6}
                                        className="px-4 py-10 text-center text-gray-400 border-t border-gray-100"
                                    >
                                        暂无毛利指标数据
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="mt-4">
                    <ExternalLink
                        href={detail.curveUrl}
                        className="!text-red-500 hover:!text-red-600 text-base font-medium"
                    >
                        投入曲线图链接
                    </ExternalLink>
                </div>

                {detail.lastReview && (
                    <div className="mt-4 rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 text-sm text-gray-600">
                        <div className="text-xs text-gray-400 mb-1">上次复盘内容（需对比改进举措是否落地）</div>
                        {detail.lastReview}
                    </div>
                )}
            </div>
        </section>
    )
}

/** 未签约：项目基本信息与人力投入（与表单同结构，只读） */
const UnsignedProjectInfo: React.FC<{ detail: UnsignedReviewDetail }> = ({ detail }) => {
    const infoItems: { label: string; value: React.ReactNode }[] = [
        { label: "项目名称（未签约）", value: text(detail.projectName) },
        { label: "项目编号", value: text(detail.projectNo) },
        { label: "所属所 / 管理所长", value: text(`${detail.department} / ${detail.manager}`) },
        { label: "复盘发送月份", value: text(detail.reviewMonth) },
        { label: "首次填报月份", value: text(detail.firstReportMonth) },
        { label: "已投入月数", value: `${detail.investedMonths} 个月` },
        // 人力投入（万元）：并入基础信息项，负数标红
        { label: "本月投入人力成本（万元）", value: <Money value={detail.monthLaborCost} unit="" /> },
        { label: "当年投入人力成本（万元）", value: <Money value={detail.yearLaborCost} unit="" /> },
        { label: "累计投入人力成本（万元）", value: <Money value={detail.totalLaborCost} unit="" /> },
    ]
    return (
        <section className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <header className="bg-[#1c4d38] px-5 py-3">
                <h3 className="text-[15px] leading-6 font-semibold text-white">项目基本信息与人力投入</h3>
            </header>
            <div className="p-5">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-8 gap-y-5">
                    {infoItems.map((it) => (
                        <ReadItem key={it.label} label={it.label}>
                            {it.value}
                        </ReadItem>
                    ))}
                </div>
            </div>
        </section>
    )
}

/** 已签约：亏损原因判定只读展示 */
const SignedConclusion: React.FC<{ payload: SignedReviewFormValue; detail: SignedReviewDetail }> = ({
    payload,
    detail,
}) => {
    // 亏损原因判定多选：兼容历史单值保存内容（reasonType）
    const reasonTypes = normalizeSignedReasonTypes(payload)
    const reasonLabel =
        reasonTypes.length > 0 ? reasonTypes.map((r) => SIGNED_REASON_LABEL[r]).join("、") : "-"
    const a1Worktimes = Array.isArray(payload.a1Worktimes) ? payload.a1Worktimes : []
    const checkedReasons = NON_BRAND_REASONS.filter((r) => payload.bReasons?.[r.key])

    return (
        <SectionCard title="亏损原因判定">
            <div className="space-y-4">
                <ReadItem label="选中大项">{reasonLabel}</ReadItem>

                {reasonTypes.includes("A") && (
                    <div className="space-y-4">
                        {payload.a1Checked && (
                            <ReadBlock title="A1 工时填报错误">
                                <A1WorktimeTable list={a1Worktimes} />
                                {payload.a1Note && (
                                    <div className="mt-3">
                                        <ReadItem label="补充说明">{payload.a1Note}</ReadItem>
                                    </div>
                                )}
                            </ReadBlock>
                        )}
                        {payload.a2Checked && (
                            <ReadBlock title="A2 里程碑填报错误">
                                <MilestoneTable list={detail.milestones ?? []} />
                                <div className="mt-3">
                                    <ExternalLink href={detail.milestoneUrl}>里程碑链接</ExternalLink>
                                </div>
                            </ReadBlock>
                        )}
                        {payload.a3Checked && (
                            <ReadBlock title="A3 立项、口径或数据源有误">
                                <div className="text-sm text-gray-800 whitespace-pre-wrap">{text(payload.a3Desc)}</div>
                            </ReadBlock>
                        )}
                        {!payload.a1Checked && !payload.a2Checked && !payload.a3Checked && (
                            <div className="text-sm text-gray-400">未勾选数据问题子项</div>
                        )}
                    </div>
                )}

                {reasonTypes.includes("B") && (
                    <div className="space-y-4">
                        {payload.bCategory === "brand" && (
                            <ReadBlock title="B0 品牌项目">
                                <div className="rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-sm text-gray-600">
                                    预计毛利率：
                                    <span
                                        className={
                                            (detail.expectedProfitRate ?? 0) < 0
                                                ? "font-semibold text-red-500"
                                                : "font-semibold text-gray-800"
                                        }
                                    >
                                        {formatPercent(detail.expectedProfitRate)}
                                    </span>
                                    <span className="text-xs text-gray-400 ml-3">
                                        当年累计完成产值毛利率：
                                        {formatPercent(
                                            detail.metrics.periods.find((p) => p.label === "当年累计")
                                                ?.completionProfitRate
                                        )}
                                    </span>
                                </div>
                                <div className="mt-3 text-sm text-gray-800 whitespace-pre-wrap">
                                    {text(payload.b0Desc)}
                                </div>
                            </ReadBlock>
                        )}
                        {payload.bCategory === "non_brand" && (
                            <ReadBlock title="非品牌项目">
                                {checkedReasons.length === 0 ? (
                                    <div className="text-sm text-gray-400">未勾选原因类别</div>
                                ) : (
                                    <div className="space-y-3">
                                        {checkedReasons.map((r) => (
                                            <div key={r.key}>
                                                <div className="flex items-center gap-1.5 text-sm text-gray-700">
                                                    <CheckOutlined className="text-dark-bg" style={{ fontSize: 12 }} />
                                                    {r.key} {r.label}
                                                </div>
                                                <div className="mt-1 pl-5 text-sm text-gray-800 whitespace-pre-wrap">
                                                    {text(payload.bReasonDesc?.[r.key])}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </ReadBlock>
                        )}
                        {payload.bCategory === "" && <div className="text-sm text-gray-400">未选择品牌 / 非品牌项目</div>}
                    </div>
                )}

                {reasonTypes.length === 0 && <div className="text-sm text-gray-400">未选择亏损原因判定</div>}
            </div>
        </SectionCard>
    )
}

/** 未签约：原因类型只读展示 */
const UnsignedConclusion: React.FC<{ payload: UnsignedReviewFormValue }> = ({ payload }) => {
    const reason = UNSIGNED_REASONS.find((r) => r.value === payload.reasonType)
    // 员工数据填错：勾选/添加的填错人员（与已签约 A1 同结构；旧保存内容缺字段时按空列表处理）
    const dataErrorWorktimes = Array.isArray(payload.dataErrorWorktimes) ? payload.dataErrorWorktimes : []
    return (
        <SectionCard title="项目情况">
            <div className="space-y-4">
                <ReadItem label="原因类型">
                    {reason ? (
                        <span>
                            {reason.label}
                            <span className="text-xs text-gray-400 ml-2">{reason.desc}</span>
                        </span>
                    ) : (
                        "-"
                    )}
                </ReadItem>

                {payload.reasonType === "wrong_initiation" && (
                    <ReadItem label="选正确项目">{text(payload.correctProjectId)}</ReadItem>
                )}
                {payload.reasonType === "data_error" && (
                    <>
                        <ReadBlock title="填错人员">
                            <A1WorktimeTable list={dataErrorWorktimes} />
                        </ReadBlock>
                        <ReadItem label="具体描述">
                            <span className="whitespace-pre-wrap">{text(payload.dataErrorDesc)}</span>
                        </ReadItem>
                        <ReadItem label="可能影响的关联项目">{text(payload.relatedProjectId)}</ReadItem>
                    </>
                )}
                {payload.reasonType === "will_sign" && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <ReadItem label="预计签约时间">{text(payload.expectedSignMonth)}</ReadItem>
                        <ReadItem label="预计签约金额（万元）">
                            {payload.expectedSignAmount === undefined || payload.expectedSignAmount === null
                                ? "-"
                                : formatMoney(payload.expectedSignAmount)}
                        </ReadItem>
                        <ReadItem label="商机状态">{text(payload.opportunityStatus)}</ReadItem>
                    </div>
                )}
                {payload.reasonType === "other" && (
                    <ReadItem label="具体描述">
                        <span className="whitespace-pre-wrap">{text(payload.otherDesc)}</span>
                    </ReadItem>
                )}
            </div>
        </SectionCard>
    )
}

const SignedReviewPreview: React.FC<{ detail: SignedReviewDetail; payload: SignedReviewFormValue }> = ({
    detail,
    payload,
}) => (
    <div className="flex-1 p-5 space-y-4">
        <SignedProjectInfo detail={detail} />
        <SignedConclusion payload={payload} detail={detail} />
        <SectionCard title="改进举措">
            <div className="text-sm text-gray-800 whitespace-pre-wrap">{text(payload.improvement)}</div>
        </SectionCard>
        {payload.attachments && payload.attachments.length > 0 && (
            <SectionCard title="附件">
                <AttachmentList attachments={payload.attachments} />
            </SectionCard>
        )}
    </div>
)

const UnsignedReviewPreview: React.FC<{ detail: UnsignedReviewDetail; payload: UnsignedReviewFormValue }> = ({
    detail,
    payload,
}) => (
    <div className="flex-1 p-5 space-y-4">
        <UnsignedProjectInfo detail={detail} />
        <UnsignedConclusion payload={payload} />
        {payload.attachments && payload.attachments.length > 0 && (
            <SectionCard title="附件">
                <AttachmentList attachments={payload.attachments} />
            </SectionCard>
        )}
    </div>
)

/** 失败态：提交内容缺失 / 读取失败 / kind 不一致时不降级为可编辑表单 */
const PreviewFailure: React.FC<{ onBack: () => void; onRetry?: () => void }> = ({ onBack, onRetry }) => (
    <div className="flex flex-col items-center justify-center py-24 gap-3">
        <div className="text-sm text-gray-500">提交内容加载失败</div>
        {onRetry && (
            <Button type="primary" onClick={onRetry}>
                重试
            </Button>
        )}
        <Button type="link" onClick={onBack}>
            返回列表
        </Button>
    </div>
)

/**
 * 只读预览页（spec 4.9）：已提交（submitted）复盘记录的只读视图。
 * 仅当记录已提交时由容器渲染；不渲染任何可输入控件，仅提供返回与外部链接 / 附件下载。
 */
const ReviewPreview: React.FC<{
    detail: ReviewDetail
    onBack: () => void
    onRetry?: () => void
}> = ({ detail, onBack, onRetry }) => {
    const saved = detail.savedSubmission
    // savedSubmission 缺失 / payload 为空 / 读取失败 / kind 不一致 → 失败态，不降级为可编辑表单
    if (
        detail.savedSubmissionError ||
        !saved ||
        saved.status !== "submitted" ||
        !saved.payload ||
        saved.kind !== detail.kind
    ) {
        return (
            <div className="min-h-full flex flex-col">
                <PreviewHeader detail={detail} onBack={onBack} />
                <PreviewFailure onBack={onBack} onRetry={onRetry} />
            </div>
        )
    }

    return (
        <div className="min-h-full flex flex-col">
            <PreviewHeader detail={detail} onBack={onBack} />
            {detail.kind === "signed" ? (
                <SignedReviewPreview detail={detail} payload={saved.payload as SignedReviewFormValue} />
            ) : (
                <UnsignedReviewPreview detail={detail} payload={saved.payload as UnsignedReviewFormValue} />
            )}
            <PreviewFooter detail={detail} onBack={onBack} />
        </div>
    )
}

export default ReviewPreview
